/**
 * A step that carries out what an AI step asked for (the ReAct "Act" step): a "Use a system" step
 * whose values come from the AI's tool call (`state.tool_call.args…`). The console explains it in
 * plain words ("Does what ‘Think’ asked for") and names the values the AI wrote ("The search text
 * ‘Think’ wrote") instead of showing `tool_call.args.query`.
 */
import type { RegistryTool, Text } from "../types";
import { referencedPaths, type Schema } from "./datamap";
import { humaniseName } from "./humanise";
import type { AgentSpec, SpecNode } from "./types";

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** Every state path a tool step's values read (templates anywhere inside its arguments). */
const argRefs = (v: unknown): string[] => referencedPaths(v);

/** Whether a tool step sends values the AI wrote (`state.tool_call.args…`). */
export function runsAiCall(node: SpecNode): boolean {
  return node.type === "tool" && argRefs(node.args ?? {}).some((p) => p === "tool_call" || p.startsWith("tool_call."));
}

const offersSystems = (n: SpecNode | undefined): boolean => !!n && n.type === "llm" && Array.isArray(n.tools) && n.tools.length > 0;

/**
 * The AI step whose tool call a step carries out: the one an arrow (or a "Choose a path" right
 * after it) sends here, else one that offers this step's system, else the only one offering any.
 */
export function aiStepFor(spec: AgentSpec, node: SpecNode): SpecNode | null {
  const nodes = spec.spec.graph.nodes;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const edges = spec.spec.graph.edges ?? [];
  for (const e of edges) {
    if (e.to !== node.id) continue;
    const src = byId.get(e.from);
    if (src && offersSystems(src)) return src;
    // Through a router whose route sends the call here.
    if (src?.type === "router")
      for (const r of edges) {
        const before = byId.get(r.from);
        if (r.to === src.id && before && offersSystems(before)) return before;
      }
  }
  const offering = nodes.filter(offersSystems);
  return offering.find((n) => (n.tools as string[]).includes(String(node.tool ?? ""))) ?? (offering.length === 1 ? (offering[0] as SpecNode) : null);
}

export interface AiChoice {
  ref: string;
  /** Whether the system is in the organisation's catalogue (unknown before it has loaded). */
  connected: boolean;
  actions: { name: string; offered: boolean }[];
}

/** The systems and actions an AI step may choose from (its systems, with the agent's grants). */
export function aiChoices(spec: AgentSpec, ai: SpecNode, tools: readonly RegistryTool[]): AiChoice[] {
  const grants = new Map((spec.spec.tools ?? []).map((g) => [g.ref, g.actions ?? []]));
  return ((ai.tools as string[] | undefined) ?? []).map((ref) => {
    const reg = tools.find((t) => t.ref === ref);
    const known = !tools.length || !!reg;
    return { ref, connected: known, actions: (grants.get(ref) ?? []).map((name) => ({ name, offered: !reg ? known : reg.actions.some((a) => a.name === name) })) };
  });
}

const SEARCH_ARGS = new Set(["query", "q", "search", "search_text", "search_query", "question"]);

function stepName(n: SpecNode | undefined, lang: "ar" | "en"): string {
  if (!n) return lang === "ar" ? "الذكاء الاصطناعي" : "the AI";
  const own = n.label && (n.label[lang] || n.label.en || n.label.ar);
  return own || humaniseName(n.id);
}

/** "The search text ‘Think’ wrote" / "The invoice id ‘Think’ wrote" for one value of the call. */
export function toolCallArgLabel(field: string, title: string | null, step: string): Text {
  if (SEARCH_ARGS.has(field.toLowerCase())) return { en: `The search text ‘${step}’ wrote`, ar: `نص البحث الذي كتبته ‘${step}’` };
  const name = title || humaniseName(field);
  return { en: `The ${name.toLowerCase()} ‘${step}’ wrote`, ar: `قيمة ‘${name}’ التي كتبتها ‘${step}’` };
}

export interface ToolCallField {
  path: string;
  label: string;
  schema: Schema;
}

/**
 * Plain names for the AI's tool call and the values in it, for the value pickers and the "What it
 * sends" rows: the values the AI may write come from the input shapes of the actions it may pick,
 * and from what the steps already read (`tool_call.args.query`).
 */
export function toolCallFields(spec: AgentSpec, tools: readonly RegistryTool[], lang: "ar" | "en", forNode?: SpecNode | null): ToolCallField[] {
  const nodes = spec.spec.graph.nodes;
  const ai = (forNode ? aiStepFor(spec, forNode) : null) ?? (nodes.filter(offersSystems).length === 1 ? nodes.find(offersSystems) : undefined);
  const s = stepName(ai ?? undefined, lang);
  const props = new Map<string, Schema>();
  const sources = ai ? [ai] : nodes.filter(offersSystems);
  for (const src of sources)
    for (const choice of aiChoices(spec, src, tools))
      for (const a of choice.actions) {
        const schema = tools.find((t) => t.ref === choice.ref)?.actions.find((x) => x.name === a.name)?.input_schema as Schema | undefined;
        const p = schema && isObj(schema.properties) ? (schema.properties as Record<string, Schema>) : {};
        for (const [k, v] of Object.entries(p)) if (!props.has(k) && isObj(v)) props.set(k, v);
      }
  for (const n of nodes)
    if (n.type === "tool")
      for (const ref of argRefs(n.args ?? {})) {
        const m = /^tool_call\.args\.([A-Za-z_]\w*)/.exec(ref);
        if (m && !props.has(m[1] as string)) props.set(m[1] as string, {});
      }
  const out: ToolCallField[] = [
    { path: "tool_call", label: lang === "ar" ? `ما طلبته ‘${s}’` : `What ‘${s}’ asked for`, schema: { type: "object" } },
    { path: "tool_call.tool", label: lang === "ar" ? `النظام الذي اختارته ‘${s}’` : `The system ‘${s}’ picked`, schema: { type: "string" } },
    { path: "tool_call.action", label: lang === "ar" ? `الإجراء الذي اختارته ‘${s}’` : `The action ‘${s}’ picked`, schema: { type: "string" } },
    { path: "tool_call.args", label: lang === "ar" ? `القيم التي كتبتها ‘${s}’` : `The values ‘${s}’ wrote`, schema: { type: "object" } },
  ];
  for (const [k, v] of props) {
    const title = typeof v.title === "string" ? v.title : null;
    out.push({ path: `tool_call.args.${k}`, label: toolCallArgLabel(k, title, s)[lang], schema: v });
  }
  return out;
}
